import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfile, logout } from '../../features/auth/authSlice';
import { fetchClasses } from '../../features/techniques/techniquesSlice';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import api from '../../utils/api';

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loading, error, isAuthenticated } = useSelector((state) => state.auth);
  const { userProgress, classes } = useSelector((state) => state.techniques);
  const fileInputRef = useRef(null);
  const [restoreError, setRestoreError] = useState('');
  const [restoreSuccess, setRestoreSuccess] = useState(false);

  // Calculate statistics
  const calculateStats = () => {
    if (!userProgress || !classes) return { totalChecks: 0, completedTechniques: 0 };
    
    let totalChecks = 0;
    let completedTechniques = 0;

    Object.values(userProgress).forEach(progress => {
      if (progress.dates) {
        totalChecks += progress.dates.filter(date => date).length;
        if (progress.dates.filter(date => date).length === 3) {
          completedTechniques += 1;
        }
      }
    });

    return {
      totalChecks,
      completedTechniques,
      stripeNumber: Math.floor(totalChecks / 20)
    };
  };

  const stats = calculateStats();

  const handleDatabaseBackup = async () => {
    try {
      const response = await api.get('/users/backup/database/', {
        responseType: 'blob'
      });
      
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // 生成文件名 (gracie_tracker_backup_YYYYMMDD_HHMMSS.json)
      const now = new Date();
      const timestamp = now.toISOString()
        .replace(/[-:]/g, '')
        .replace('T', '_')
        .split('.')[0];
      
      link.setAttribute('download', `gracie_tracker_backup_${timestamp}.json`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to backup database:', error);
    }
  };

  const handleDatabaseRestore = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 验证文件类型
    if (!file.name.endsWith('.json')) {
      setRestoreError('Invalid file type. Please select a JSON file.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post('/users/restore/database/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setRestoreSuccess(true);
      setRestoreError('');
      
      // 3秒后刷新页面
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      setRestoreError(error.response?.data?.error || 'Failed to restore database');
      setRestoreSuccess(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      if (!user) {
        dispatch(fetchProfile());
      }
      dispatch(fetchClasses());
    }
  }, [isAuthenticated, user, dispatch, navigate]);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
          <Avatar
            sx={{ width: 100, height: 100, mb: 2 }}
          >
            {user.username?.[0]?.toUpperCase()}
          </Avatar>
          <Typography component="h1" variant="h4" gutterBottom>
            {user.username}
          </Typography>
          <Typography color="textSecondary" gutterBottom>
            {user.email}
          </Typography>
          <Chip
            icon={user?.is_superuser ? <AdminPanelSettingsIcon /> : <PersonIcon />}
            label={user?.is_superuser ? "Administrator" : "User"}
            color={user?.is_superuser ? "primary" : "default"}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Belt Rank"
              value={user.belt_rank || ''}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Academy"
              value={user.academy || ''}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Start Date"
              value={user.start_date || ''}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Training Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Belt Color"
                  value={user.belt_rank || 'White Belt'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Completed Classes"
                  value={stats.totalChecks || 0}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Stripe Number"
                  value={stats.stripeNumber || 0}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Learned Techniques"
                  value={stats.completedTechniques || 0}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/profile/edit')}
          >
            Edit Profile
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              dispatch(logout());
              navigate('/login');
            }}
          >
            Logout
          </Button>
        </Box>

        {user?.is_superuser && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Administrator Actions
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleDatabaseBackup}
              >
                Backup Database
              </Button>
              
              <input
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleDatabaseRestore}
              />
              <Button
                variant="contained"
                color="warning"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current.click()}
              >
                Restore Database
              </Button>
            </Stack>

            {restoreError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {restoreError}
              </Alert>
            )}
            
            {restoreSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Database restored successfully. Page will refresh in 3 seconds...
              </Alert>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default UserProfile;
